import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ApiClientError, getCaptcha, login, register } from '@suangua/shared';
import { useAuth } from '../../lib/auth/AuthContext';

export interface AuthModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * 登录 / 注册弹窗（玄墨鎏金 modal 风格）
 * 注册与登录同弹层切换；注册需算术验证码（PLAN §6.6）。
 */
export function AuthModal({ open, onClose }: AuthModalProps) {
  const { setUser } = useAuth();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [account, setAccount] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [captchaSvg, setCaptchaSvg] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /** 拉取新验证码（注册模式展示） */
  const loadCaptcha = useCallback(async () => {
    try {
      const res = await getCaptcha();
      setCaptchaId(res.captcha_id);
      setCaptchaSvg(res.svg);
      setCaptchaAnswer('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '验证码获取失败');
    }
  }, []);

  useEffect(() => {
    if (open && mode === 'register') void loadCaptcha();
  }, [open, mode, loadCaptcha]);

  // 打开时重置错误态
  useEffect(() => {
    if (open) setError('');
  }, [open]);

  if (!open) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res =
        mode === 'login'
          ? await login({ account, password })
          : await register({
              email,
              password,
              username,
              captcha_id: captchaId,
              captcha_answer: Number(captchaAnswer),
            });
      setUser(res.user);
      onClose();
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : '操作失败，请稍后再试';
      setError(message);
      // 验证码被消费（无论对错），换新题
      if (mode === 'register' && err instanceof ApiClientError) void loadCaptcha();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-wrap modal-sm">
      <div className="modal-mask" onClick={onClose} />
      <div className="modal-panel">
        <div className="modal-head">
          <p className="modal-title font-zh">{mode === 'login' ? '登录' : '注册'}</p>
          <button className="icon-btn" onClick={onClose} aria-label="关闭">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {/* 登录 / 注册切换 */}
        <div className="seg">
          <button className={mode === 'login' ? 'on' : ''} onClick={() => setMode('login')}>
            登录
          </button>
          <button className={mode === 'register' ? 'on' : ''} onClick={() => setMode('register')}>
            注册
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          {mode === 'login' ? (
            <input
              className="field"
              placeholder="邮箱或昵称"
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              required
            />
          ) : (
            <>
              <input
                className="field"
                type="email"
                placeholder="邮箱（仅校验格式）"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                className="field"
                placeholder="昵称（2~16 字符）"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </>
          )}

          <input
            className="field"
            type="password"
            placeholder="密码（至少 8 位）"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          {mode === 'register' && captchaSvg && (
            <div className="flex items-center gap-2">
              {/* 验证码 SVG：防止 XSS，仅渲染服务端返回的受控 SVG 字符串 */}
              <div dangerouslySetInnerHTML={{ __html: captchaSvg }} className="captcha-svg" />
              <input
                className="field"
                inputMode="numeric"
                placeholder="算式答案"
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value)}
                required
              />
              <button type="button" className="vc shrink-0" onClick={() => void loadCaptcha()}>
                换一个
              </button>
            </div>
          )}

          {error && <p className="text-[11px] text-cinnabar-500">{error}</p>}

          <button type="submit" className="gold-btn" disabled={submitting}>
            <i className={`fa-solid ${mode === 'login' ? 'fa-right-to-bracket' : 'fa-user-plus'}`} />
            {submitting ? '提交中…' : mode === 'login' ? '登录' : '注册'}
          </button>
        </form>

        <p className="mt-4 text-center text-[11px] text-t3">
          登录后可发帖、回复、点赞与保存占卜历史
        </p>
      </div>
    </div>
  );
}
